import React from "react";
import { Search, X } from "lucide-react";

export default function EventDiscoveryFilters({
  search,
  onSearchChange,
  dateFilter,
  onDateFilterChange,
  dateOptions,
  category,
  onCategoryChange,
  categoryOptions,
  resultCount,
  onClear,
}) {
  const hasActiveFilters =
    Boolean(search.trim()) || dateFilter !== "all" || category !== "all";

  return (
    <section
      aria-labelledby="event-discovery-heading"
      className="rounded-2xl border border-white/10 bg-white/5 p-4 md:p-5"
    >
      <div className="flex flex-col gap-4">
        <div>
          <h2 id="event-discovery-heading" className="text-lg font-semibold">
            Find an event
          </h2>
          <p className="mt-1 text-sm text-white/65">
            Search by event, venue or organiser, then narrow the dates if useful.
          </p>
        </div>

        <div className={`grid gap-3 ${categoryOptions.length ? "md:grid-cols-[minmax(0,1fr)_15rem]" : ""}`}>
          <label className="block min-w-0">
            <span className="mb-1 block text-sm font-medium text-white/80">Search events</span>
            <span className="relative block">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/45"
                aria-hidden="true"
              />
              <input
                type="search"
                value={search}
                onChange={(event) => onSearchChange(event.target.value)}
                placeholder="Try football, family or Phoenix Hub"
                className="min-h-11 w-full rounded-xl border border-white/20 bg-black/40 py-2 pl-10 pr-3 text-sm placeholder:text-white/40"
              />
            </span>
          </label>

          {categoryOptions.length ? (
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-white/80">Category</span>
              <select
                value={category}
                onChange={(event) => onCategoryChange(event.target.value)}
                className="min-h-11 w-full rounded-xl border border-white/20 bg-black/40 px-3 py-2 text-sm"
              >
                <option value="all">All categories</option>
                {categoryOptions.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </label>
          ) : null}
        </div>

        <div>
          <p className="mb-2 text-sm font-medium text-white/80">When</p>
          <div
            role="group"
            aria-label="Filter events by date"
            className="-mx-1 flex snap-x gap-2 overflow-x-auto px-1 pb-2"
          >
            {dateOptions.map((option) => {
              const selected = option.value === dateFilter;
              return (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => onDateFilterChange(option.value)}
                  className={`min-h-11 shrink-0 snap-start rounded-full border px-4 py-2 text-sm font-semibold transition ${
                    selected
                      ? "border-[#F4A261] bg-[#F4A261] text-slate-950"
                      : "border-white/15 bg-black/30 text-white/80 hover:bg-white/10"
                  }`}
                >
                  {option.label} <span aria-hidden="true">{option.count}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex min-h-8 flex-wrap items-center justify-between gap-2 border-t border-white/10 pt-3">
          <p role="status" aria-live="polite" className="text-sm text-white/70">
            {resultCount} {resultCount === 1 ? "event" : "events"} found
          </p>
          {hasActiveFilters ? (
            <button
              type="button"
              onClick={onClear}
              className="inline-flex min-h-10 items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-white/80 hover:bg-white/10"
            >
              <X className="h-4 w-4" aria-hidden="true" />
              Clear filters
            </button>
          ) : null}
        </div>
      </div>
    </section>
  );
}
